import { dayjs } from "@/core/utils/date";
import type { IAiSpreadsheetCell } from "@/payroll/ai-script/types";
import { getCellErrorCode, normalizeSingleLineCellText } from "./utils";

interface SimpleNumericFormat {
  currency: boolean;
  decimalPlaces: number;
  percent: boolean;
  useGrouping: boolean;
}

const SUPPORTED_EXCEL_DATE_FORMAT_CODES = new Set([
  "m/d/yy",
  "mm/dd/yy",
  "m/d/yyyy",
  "mm/dd/yyyy",
  "d/m/yy",
  "dd/mm/yy",
  "d/m/yyyy",
  "dd/mm/yyyy",
  "m-d-yy",
  "mm-dd-yy",
  "m-d-yyyy",
  "mm-dd-yyyy",
  "d-m-yy",
  "dd-mm-yy",
  "d-m-yyyy",
  "dd-mm-yyyy",
  "yyyy-m-d",
  "yyyy-mm-dd",
  "yyyy/m/d",
  "yyyy/mm/dd",
  "d-mmm-yy",
  "dd-mmm-yy",
  "d-mmm-yyyy",
  "dd-mmm-yyyy",
  "mmm d, yy",
  "mmm d, yyyy",
  "mmmm d, yy",
  "mmmm d, yyyy",
]);
const EXCEL_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const EXCEL_EPOCH_BEFORE_LEAP_DAY = "1899-12-31";
const EXCEL_EPOCH_AFTER_LEAP_DAY = "1899-12-30";
const MAX_EXCEL_DATE_SERIAL = 2_958_465;

export const parseAiSpreadsheetDateValue = (
  value: string | number | boolean,
) => {
  if (typeof value === "boolean") return null;
  if (typeof value === "string" && value.trim() === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    const serial = value;
    const wholeDays = Math.trunc(serial);
    if (wholeDays < 0 || wholeDays > MAX_EXCEL_DATE_SERIAL) return null;

    // Excel treats 1900 as a leap year. Match its serial-date compatibility by
    // shifting the epoch after the fictitious 1900-02-29 (serial 60).
    const epoch =
      wholeDays < 60 ? EXCEL_EPOCH_BEFORE_LEAP_DAY : EXCEL_EPOCH_AFTER_LEAP_DAY;
    return dayjs.utc(epoch).add(wholeDays, "day");
  }

  const date = dayjs.utc(value, "YYYY-MM-DD", true);
  return date.isValid() ? date : null;
};

const formatExcelDateValue = (
  value: string | number | boolean,
  formatCode: string,
): string | null => {
  const normalizedFormatCode = formatCode.trim().toLowerCase();

  if (!SUPPORTED_EXCEL_DATE_FORMAT_CODES.has(normalizedFormatCode)) return null;
  const date = parseAiSpreadsheetDateValue(value);
  if (!date) return null;

  const day = date.date();
  const month = date.month() + 1;
  const year = date.year();
  const monthName = EXCEL_MONTH_NAMES[month - 1];
  const parts: Record<string, string> = {
    yyyy: String(year).padStart(4, "0"),
    mmmm: monthName,
    mmm: monthName.slice(0, 3),
    yy: String(year).slice(-2).padStart(2, "0"),
    mm: String(month).padStart(2, "0"),
    m: String(month),
    dd: String(day).padStart(2, "0"),
    d: String(day),
  };

  return normalizedFormatCode.replace(
    /yyyy|mmmm|mmm|yy|mm|m|dd|d/g,
    (token) => parts[token],
  );
};

const UNSUPPORTED_FORMAT_CODE_CHARACTERS = [
  ";",
  "[",
  "]",
  "_",
  "*",
  "@",
  "(",
  ")",
];
const SIMPLE_NUMERIC_FORMAT_CODE_PATTERN = /^(#,##)?0(?:\.(0+))?$/;

const parseNumericFormatCode = (
  formatCode: string,
): SimpleNumericFormat | null => {
  const trimmed = formatCode.trim();

  if (
    !trimmed ||
    UNSUPPORTED_FORMAT_CODE_CHARACTERS.some((character) =>
      trimmed.includes(character),
    ) ||
    /e/i.test(trimmed)
  ) {
    return null;
  }

  const currency = trimmed.startsWith("$");
  const percent = trimmed.endsWith("%");
  const numericPattern = trimmed.replace(/^\$/, "").replace(/%$/, "");
  const match = numericPattern.match(SIMPLE_NUMERIC_FORMAT_CODE_PATTERN);

  if (!match || (currency && percent)) return null;

  return {
    currency,
    decimalPlaces: match[2]?.length ?? 0,
    percent,
    useGrouping: Boolean(match[1]),
  };
};

// Render the payroll subset of Excel format codes; fall back to the raw
// computed value for anything more complex than the viewer can safely interpret.
export const formatAiSpreadsheetValue = (
  value: string | number | boolean,
  formatCode: string,
): string => {
  const formattedDate = formatExcelDateValue(value, formatCode);

  if (formattedDate !== null) return formattedDate;

  const numeric = typeof value === "number" ? value : Number(value);

  if (typeof value === "boolean" || Number.isNaN(numeric)) return String(value);

  const parsed = parseNumericFormatCode(formatCode);

  if (!parsed) return String(value);

  const numericValue = parsed.percent ? numeric * 100 : numeric;
  const absoluteValue = parsed.currency ? Math.abs(numericValue) : numericValue;
  const formatted = absoluteValue.toLocaleString("en-US", {
    minimumFractionDigits: parsed.decimalPlaces,
    maximumFractionDigits: parsed.decimalPlaces,
    useGrouping: parsed.useGrouping,
  });

  if (parsed.currency) {
    return `${numericValue < 0 ? "-" : ""}$${formatted}`;
  }

  return parsed.percent ? `${formatted}%` : formatted;
};

export const getAiSpreadsheetCellDisplayValue = (cell: IAiSpreadsheetCell) => {
  const errorCode = getCellErrorCode(cell);

  if (errorCode) return errorCode;

  if (cell.computed_value === null || cell.computed_value === undefined) {
    return "";
  }

  const formatCode = cell.format?.format_code;

  const displayValue = formatCode
    ? formatAiSpreadsheetValue(cell.computed_value, formatCode)
    : String(cell.computed_value);

  return normalizeSingleLineCellText(displayValue, cell);
};
